/**
 * Kleinteile des Cockpits — Balken, Trendpfeil, Karte, Dialoghuelle.
 *
 * Sie stehen AUSSERHALB jeder Komponente (Befund 15 des Umbauplans): frueher
 * waren `Bar`/`Ring`/`Chart` im Rumpf von AdminDashboard deklariert, dadurch
 * war bei jedem Hover die Identitaet eine andere und React haengte den ganzen
 * Teilbaum neu an.
 *
 * Ein Tiefenmittel je Element: Rahmen ODER Schatten. Im Fluss traegt der
 * Rahmen, geworfen wird nur vom Dialog.
 */
import React from 'react';
import { ArrowDownRight, ArrowUpRight, X } from 'lucide-react';

export const Bar: React.FC<{ value: number; max: number; color?: string; height?: string }> = ({
    value, max, color = 'bg-emerald-500', height = 'h-1.5',
}) => (
    <div className={`w-full ${height} bg-slate-100 rounded-full overflow-hidden`}>
        <div
            className={`h-full rounded-full transition-all duration-500 ${color}`}
            style={{ width: `${Math.min(100, max > 0 ? (value / max) * 100 : 0)}%` }}
        />
    </div>
);

/** Veraenderung gegenueber dem Vormonat. `prev === 0` ist kein Wachstum von 100 %. */
export const Trend: React.FC<{ current: number; previous: number }> = ({ current, previous }) => {
    if (previous <= 0) return <span className="text-[11px] text-slate-400">—</span>;
    const delta = Math.round(((current - previous) / previous) * 100);
    if (delta > 0) {
        return (
            <span className="text-[11px] font-semibold text-emerald-600 flex items-center gap-0.5">
                <ArrowUpRight className="w-3 h-3" />+{delta}%
            </span>
        );
    }
    if (delta < 0) {
        return (
            <span className="text-[11px] font-semibold text-red-500 flex items-center gap-0.5">
                <ArrowDownRight className="w-3 h-3" />{delta}%
            </span>
        );
    }
    return <span className="text-[11px] text-slate-400">—</span>;
};

export const Panel: React.FC<{
    title: string;
    icon?: React.ReactNode;
    hint?: string;
    right?: React.ReactNode;
    children: React.ReactNode;
    bodyClassName?: string;
}> = ({ title, icon, hint, right, children, bodyClassName = 'p-5' }) => (
    <div data-glass="cockpit-panel" className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex justify-between items-center gap-3">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 min-w-0">
                {icon}<span className="truncate">{title}</span>
                {hint && <span className="text-[11px] text-slate-400 font-normal shrink-0">{hint}</span>}
            </h3>
            {right}
        </div>
        <div className={bodyClassName}>{children}</div>
    </div>
);

/** Dialoghuelle: das einzige Element, das werfen darf. */
export const ModalShell: React.FC<{
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    width?: string;
    onClose: () => void;
    footer?: React.ReactNode;
    children: React.ReactNode;
}> = ({ title, subtitle, width = 'max-w-3xl', onClose, footer, children }) => (
    <div
        data-glass-view="projekt-dialog"
        className="fixed inset-0 z-[660] bg-black/60 animate-fade-in flex items-center justify-center p-4"
        onClick={onClose}
    >
        <div
            className={`bg-white rounded-2xl shadow-lg w-full ${width} max-h-[85vh] flex flex-col`}
            onClick={(e) => e.stopPropagation()}
        >
            <div className="bg-slate-800 text-white px-6 py-4 flex justify-between items-start gap-4 shrink-0 rounded-t-2xl">
                <div className="min-w-0">
                    <h3 className="font-semibold text-base truncate">{title}</h3>
                    {subtitle && <div className="mt-1 flex flex-wrap gap-3">{subtitle}</div>}
                </div>
                <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg shrink-0" title="Schließen">
                    <X className="w-5 h-5" />
                </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-5">{children}</div>
            {footer && <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex justify-between items-center gap-3 shrink-0 rounded-b-2xl">{footer}</div>}
        </div>
    </div>
);
